import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService, AuthService } from '@homeleite/arch';
import { jwtDecode } from 'jwt-decode';
import { finalize, timeout } from 'rxjs/operators';
import { currentApiEnvKey, currentApiEnvironment } from '../../core/api-environments';

interface SyncItem {
    animalId: string;
    animalCode: string | null;
    producerId: string | null;
    producerCode?: string | null;
    producerName?: string | null;
    status: 'PENDING' | 'SYNCED' | 'FAILED' | 'UNSENT';
    attempts: number;
    lastAttemptAt: string | null;
    syncedAt: string | null;
    lastError: string | null;
    manual: boolean;
    srgSclCode?: string | null;
    srgSclReason?: string | null;
    details?: Record<string, any>;
}

interface OutboxItem {
    id: string;
    kind: string;
    eventId: string;
    animalId: string;
    status: 'PENDING' | 'SENT' | 'FAILED' | 'DISCARDED';
    attempts: number;
    queuedAt: string | null;
    lastAttemptAt: string | null;
    sentAt: string | null;
    lastError: string | null;
    srgSclCode: string | null;
    srgSclReason: string | null;
    createdBy?: string | null;
    createdByActor?: 'PRODUCER' | 'CONTROLLER' | 'ADMIN' | null;
    failures?: OutboxFailure[];
    lastSrgFailureAt?: string | null;
    srgFailureCount?: number;
}

interface OutboxFailure {
    at: string | null;
    httpStatus: number | null;
    kind: string | null;
    error: string | null;
}

interface OutboxResponse {
    counts: Record<string, number>;
    items: OutboxItem[];
}

const EVENT_LABELS: Record<string, string> = {
    Pregnancy: 'Prenhez',
    ChildBirth: 'Parto',
    Mating: 'Cobertura',
    Dry: 'Secagem',
    Abortion: 'Aborto',
    HerdOut: 'Baixa'
};

const DIVERGENCE_LABELS: Record<string, string> = {
    'animals.canceladoNoSrg': 'Ausente no SRG-SCL (cancelado, vendido ou transferido)',
    'animals.vivoSoNoHL': 'Vivo só no HomeLeite',
    'animals.duplicados': 'Animais duplicados',
    'animals.baixaContraditoria': 'Baixa com evento posterior'
};

interface SyncResponse {
    counts: Record<string, number>;
    items: SyncItem[];
}

interface DivergenceItem {
    producerId: string;
    producerCode: string | null;
    producerName: string | null;
    kind: string;
    missingCount: number;
    sample: string[];
    firstDetectedAt: string | null;
    lastDetectedAt: string | null;
    tenantId?: string | null;
    animals?: DivergenceAnimal[];
}

interface DivergenceAnimal {
    id: string;
    eventId: string | null;
    name: string | null;
    nickname: string | null;
    register: string | null;
    apcId: string | null;
    active: boolean | null;
}

interface DivergenceResponse {
    count: number;
    items: DivergenceItem[];
}

interface OriginMetric {
    origin: string;
    pending: number;
    sent: number;
    failed: number;
    discarded: number;
    avgLatencyMs: number | null;
}

interface MetricsResponse {
    days: number;
    origins: OriginMetric[];
}

interface SyncFailureItem {
    id: string;
    producerId: string | null;
    animalId: string | null;
    actorType: string | null;
    at: string | null;
    error: string | null;
    entityId?: string | null;
    status?: string | null;
    url?: string | null;
}

interface SyncFailuresResponse {
    count: number;
    items: SyncFailureItem[];
}

interface RetryResult {
    animalId: string;
    ok: boolean;
    error: string | null;
}

interface TenantHealth {
    tenantId: string;
    enabled: boolean;
    activeFetch: { lastOkAt: string | null; failedLots6h: number };
    outbox: Record<string, any>;
    divergences: number;
    alerts: string[];
}

type ViewState = 'loading' | 'error' | 'unavailable' | 'ready';
type Filter = '' | 'FAILED' | 'SYNCED' | 'PENDING' | 'UNSENT';


@Component({
    selector: 'homeleite-integration-monitor',
    templateUrl: './integration-monitor.component.html',
    styleUrls: ['./integration-monitor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IntegrationMonitorComponent implements OnInit {
    readonly pageSize = 50;
    readonly labels: Record<string, string> = { FAILED: 'Com erro', SYNCED: 'Enviado', PENDING: 'Pendente', UNSENT: 'Não enviado' };
    state: ViewState = 'loading';
    filter: Filter = 'SYNCED';
    page = 0;
    data: SyncResponse = { counts: {}, items: [] };
    retrying = new Set<string>();
    retryingAll = false;
    rechecking = new Set<string>();
    retryingOutbox = false;
    search = '';
    readonly outboxLabels: Record<string, string> = { PENDING: 'Pendente', SENT: 'Enviado', FAILED: 'Com erro', DISCARDED: 'Descartado' };
    readonly actorLabels: Record<string, string> = { PRODUCER: 'Produtor', CONTROLLER: 'Controlador', ADMIN: 'Admin' };
    readonly outboxPageSize = 50;
    outboxState: ViewState = 'loading';
    outboxLoadingMore = false;
    // Show sent and pending events on first open; the failure KPI remains
    // available as a one-click filter.
    outboxFilter = '';
    outbox: OutboxResponse = { counts: {}, items: [] };
    outboxBusy = new Set<string>();
    outboxExpanded = new Set<string>();
    readonly failureKindLabels: Record<string, string> = {
        SRG_SCL: 'Erro no SRG-SCL',
        REDE: 'Falha de rede',
        RECUSA: 'Recusado',
        INTERNO: 'Erro interno'
    };
    healthState: ViewState = 'loading';
    health: TenantHealth[] = [];

    get tenantIds(): string[] {
        return this.health.map((h) => h.tenantId);
    }
    readonly healthAlertLabels: Record<string, string> = {
        FILA_FAILED: 'Itens da fila com erro',
        FILA_PARADA: 'Fila parada há mais de 15 min',
        BUSCA_ATIVA_ATRASADA: 'Busca ativa sem sucesso há mais de 1 h',
        BUSCA_ATIVA_COM_FALHA: 'Busca ativa com falha nas últimas 6 h',
        WEBHOOK_SEM_EVENTO_24H: 'Nenhum evento recebido há mais de 24 h',
        CONFIG_ILEGIVEL: 'Configuração da integração ilegível'
    };
    divergenceState: ViewState = 'loading';
    divergences: DivergenceResponse = { count: 0, items: [] };
    metricsState: ViewState = 'loading';
    metrics: MetricsResponse = { days: 7, origins: [] };
    readonly originLabels: Record<string, string> = { PRODUCER: 'Produtor', CONTROLLER: 'Controlador', ADMIN: 'Admin', LEGADO: 'Anterior ao registro de origem' };
    failuresState: ViewState = 'loading';
    failures: SyncFailuresResponse = { count: 0, items: [] };
    readonly failureBusy = new Set<string>();
    /** Alterna painel: eventos × animais (evita página única sem rolagem útil). */
    viewMode: 'events' | 'animals' | 'inbound' | 'lots' | 'webhook' = 'events';

    divergenceProducer = '';

    constructor(private api: ApiService, private auth: AuthService, private snackBar: MatSnackBar, private cd: ChangeDetectorRef) {}

    private currentTenantId(): string {
        try {
            return jwtDecode<{ tenantId?: string }>(this.auth.currentToken).tenantId ?? '';
        } catch {
            return '';
        }
    }

    ngOnInit(): void {
        this.load();
        this.loadOutbox();
        this.loadDivergences();
        this.loadMetrics();
        this.loadFailures();
        this.loadHealth();
    }

    loadHealth(): void {
        this.healthState = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'health')
            .getJson<TenantHealth[]>()
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.health = r;
                    this.healthState = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.healthState = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    healthAlertLabel(a: string): string {
        return this.healthAlertLabels[a] || a;
    }

    loadMetrics(): void {
        this.metricsState = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'metrics')
            .getJson<MetricsResponse>({ params: new HttpParams().set('days', this.metrics.days) })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.metrics = r;
                    this.metricsState = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.metricsState = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    loadFailures(): void {
        this.failuresState = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'sync-failures')
            .getJson<SyncFailuresResponse>({ params: new HttpParams().set('size', 50) })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.failures = r;
                    this.failuresState = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.failuresState = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    resolveFailure(f: SyncFailureItem): void {
        if (!this.confirmEnv('Marcar esta falha como resolvida? Ela sai da lista.')) return;
        this.failureBusy.add(f.id);
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'sync-failures', 'resolve')
            .postJson<{ resolved: number }>({ failureIds: [f.id] })
            .pipe(
                timeout(15000),
                finalize(() => {
                    this.failureBusy.delete(f.id);
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: () => this.loadFailures(),
                error: () =>
                    this.snackBar.open('Falha na operação (API indisponível ou tempo esgotado)', 'OK', {
                        duration: 5000
                    })
            });
    }

    originMetricLabel(origin: string): string {
        return this.originLabels[origin] || origin;
    }

    /** Taxa de sucesso sobre o que já foi concluído (enviado ou com erro). Null quando ainda não há conclusão. */
    successRate(m: OriginMetric): number | null {
        const done = m.sent + m.failed;
        return done === 0 ? null : Math.round((m.sent / done) * 100);
    }

    latencyLabel(ms: number | null): string {
        if (ms === null || ms === undefined) return '—';
        return ms < 60000 ? `${Math.round(ms / 1000)} s` : `${Math.round(ms / 60000)} min`;
    }

    recheckDivergence(d: DivergenceItem): void {
        const key = d.producerId + d.kind;
        if (!d.tenantId || this.rechecking.has(key)) return;
        this.rechecking.add(key);
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'divergences', 'recheck')
            .postJson<{ ok: boolean; applied?: number; items?: DivergenceItem[] }>({}, { params: new HttpParams().set('tenantId', d.tenantId).set('producerId', d.producerId) })
            .pipe(
                timeout(120000),
                finalize(() => {
                    this.rechecking.delete(key);
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r) => {
                    this.snackBar.open(this.recheckMessage(d, r?.applied ?? 0, r?.items), 'OK', { duration: 8000 });
                    this.loadDivergences();
                },
                error: () => this.snackBar.open('Falha ao reconferir (produtor não encontrado ou SRG-SCL indisponível)', 'OK', { duration: 6000 })
            });
    }

    private recheckMessage(d: DivergenceItem, applied: number, items?: DivergenceItem[]): string {
        const baixas = applied > 0 ? `${applied} baixa(s) do SRG-SCL aplicada(s). ` : '';
        if (!items) return `${baixas}Conferência refeita; divergências atualizadas`;
        const same = items.find((i) => i.kind === d.kind);
        if (!same) return `${baixas}Divergência resolvida.`;
        const delta = d.missingCount - same.missingCount;
        const prefix = delta > 0 ? `${delta} resolvida(s); ` : 'Nada mudou: ';
        return `${baixas}${prefix}continua(m) ${same.missingCount} (${this.divergenceKindLabel(same.kind)}).${same.kind === 'animals.vivoSoNoHL' ? ' Sem baixa ou cancelamento no SRG-SCL que explique.' : ''}`;
    }

    private divergenceParams(): HttpParams {
        let params = new HttpParams().set('limit', 50);
        const tenantId = this.currentTenantId();
        if (tenantId) params = params.set('tenantId', tenantId);
        const producer = this.divergenceProducer.trim();
        if (producer) params = params.set('producerCode', producer);
        return params;
    }

    loadDivergences(): void {
        this.divergenceState = 'loading';
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'divergences')
            .getJson<DivergenceResponse>({ params: this.divergenceParams() })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.divergences = r;
                    this.divergenceState = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.divergenceState = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    divergenceKindLabel(kind: string): string {
        return DIVERGENCE_LABELS[kind] || this.eventLabel(kind);
    }

    daysSince(iso: string | null | undefined): number {
        if (!iso) return 0;
        return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    }

    setViewMode(mode: 'events' | 'animals' | 'inbound' | 'lots' | 'webhook'): void {
        if (this.viewMode === mode) {
            return;
        }
        this.viewMode = mode;
        this.cd.markForCheck();
    }

    eventLabel(kind: string): string {
        const name = kind.substring(kind.lastIndexOf('.') + 1);
        return EVENT_LABELS[name] || name;
    }

    shortId(value: string | null | undefined): string {
        if (!value) return '—';
        return value.length > 12 ? `${value.slice(0, 8)}…` : value;
    }

    refreshAll(): void {
        this.load();
        this.loadOutbox();
        this.loadDivergences();
        this.loadMetrics();
        this.loadFailures();
        this.loadHealth();
    }

    get outboxTotal(): number {
        const c = this.outbox.counts || {};
        return (c['PENDING'] || 0) + (c['SENT'] || 0) + (c['FAILED'] || 0) + (c['DISCARDED'] || 0);
    }

    toggleFailures(ev: OutboxItem): void {
        if (this.outboxExpanded.has(ev.id)) this.outboxExpanded.delete(ev.id);
        else this.outboxExpanded.add(ev.id);
    }

    receiptOk(code: string | null | undefined): boolean {
        return code === '200';
    }

    setOutboxFilter(f: string): void {
        this.viewMode = 'events';
        this.outboxFilter = f;
        this.loadOutbox();
        this.cd.markForCheck();
    }

    loadOutbox(): void {
        this.outboxState = 'loading';
        this.cd.markForCheck();
        this.fetchOutbox(0).subscribe({
            next: (r) => {
                this.outbox = r;
                this.outboxState = 'ready';
                this.cd.markForCheck();
            },
            error: (e) => {
                this.outboxState = e?.status === 404 ? 'unavailable' : 'error';
                this.cd.markForCheck();
            }
        });
    }

    /** Próxima página do outbox, somada à atual. Não recarrega do início. */
    loadMoreOutbox(): void {
        if (this.outboxLoadingMore) return;
        this.outboxLoadingMore = true;
        this.cd.markForCheck();
        this.fetchOutbox(this.outbox.items.length).pipe(
            finalize(() => {
                this.outboxLoadingMore = false;
                this.cd.markForCheck();
            })
        ).subscribe({
            next: (r) => {
                this.outbox = { counts: r.counts, items: [...this.outbox.items, ...r.items] };
                this.cd.markForCheck();
            },
            error: () => this.snackBar.open('Não foi possível carregar mais eventos', 'OK', { duration: 5000 })
        });
    }

    get outboxHasMore(): boolean {
        return this.outbox.items.length < this.outboxTotalForFilter;
    }

    /** Total do filtro atual: o contador de status quando há filtro, senão o total geral. */
    private get outboxTotalForFilter(): number {
        const c = this.outbox.counts || {};
        return this.outboxFilter ? c[this.outboxFilter] || 0 : this.outboxTotal;
    }

    originLabel(ev: OutboxItem): string {
        if (!ev.createdBy) return 'Origem não registrada';
        const actor = ev.createdByActor ? this.actorLabels[ev.createdByActor] || ev.createdByActor : null;
        return actor ? `${actor}: ${ev.createdBy}` : ev.createdBy;
    }

    private fetchOutbox(skip: number) {
        let params = new HttpParams().set('limit', this.outboxPageSize).set('skip', skip);
        if (this.outboxFilter) params = params.set('status', this.outboxFilter);
        return this.api.root
            .path('integration-sync', 'outbox')
            .getJson<OutboxResponse>({ params })
            .pipe(timeout(15000));
    }

    retryAllOutbox(): void {
        const count = this.outbox.counts['FAILED'] || 0;
        if (!count) return;
        if (!this.confirmEnv(`Reenviar os ${count} evento(s) com erro da fila de saída?`)) return;
        this.retryingOutbox = true;
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'outbox', 'retry')
            .postJson<{ retried: number; requiresConfirmation?: boolean; count?: number }>({ confirmCount: count })
            .pipe(
                timeout(60000),
                finalize(() => {
                    this.retryingOutbox = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: (r) => {
                    if (r.requiresConfirmation) {
                        this.snackBar.open(`A contagem mudou para ${r.count}. Nada foi reenviado; confira e tente de novo.`, 'OK', { duration: 7000 });
                    } else {
                        this.outboxFilter = 'PENDING';
                        this.snackBar.open(`${r.retried} evento(s) voltaram para a fila`, 'OK', { duration: 6000 });
                    }
                    this.loadOutbox();
                },
                error: () => this.failed()
            });
    }

    reasonPt(ev: OutboxItem): string {
        const text = `${ev.srgSclReason || ''} ${ev.lastError || ''}`.toLowerCase();
        if (text.includes('idade mínima')) return 'Animal abaixo da idade mínima exigida pelo SRG-SCL.';
        if (text.includes('serviço não disponível') || text.includes('http error [5')) return 'SRG-SCL indisponível; o app tenta de novo sozinho.';
        if (text.includes('timeout') || text.includes('timed out')) return 'Tempo esgotado ao falar com o SRG-SCL.';
        if (text.includes('401') || text.includes('403') || text.includes('unauthorized')) return 'SRG-SCL recusou a credencial da associação.';
        if (text.includes('não possui') || text.includes('invalid') || text.includes('regra')) return 'Dado recusado por regra do SRG-SCL; corrija o cadastro.';
        return '';
    }

    isRuleError(item: OutboxItem): boolean {
        const text = `${item.srgSclCode || ''} ${item.srgSclReason || ''} ${item.lastError || ''}`.toLowerCase();
        return (
            item.srgSclCode === '500' ||
            text.includes('idade mínima') ||
            text.includes('regra') ||
            text.includes('não possui') ||
            text.includes('invalid') ||
            text.includes('rejeit')
        );
    }

    outboxAction(item: OutboxItem, action: 'retry' | 'discard'): void {
        if (action === 'retry' && this.isRuleError(item)) {
            const go = confirm(
                'Este erro é de regra do SRG-SCL (ex.: idade mínima).\n\n' +
                    'Reenviar só ajuda se o cadastro/evento for corrigido no Home+Leite.\n' +
                    'Se o evento for inválido de fato, use Descartar.\n\n' +
                    'Deseja reenviar mesmo assim?'
            );
            if (!go) return;
        }
        const msg =
            action === 'retry'
                ? 'Reenviar este evento ao SRG-SCL?'
                : 'Descartar este evento (não será mais enviado)?';
        if (
            action === 'discard'
                ? !confirm(`${msg}\n\nAmbiente: ${currentApiEnvironment().label}`)
                : !this.confirmEnv(msg)
        ) {
            return;
        }
        this.outboxBusy.add(item.id);
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'outbox', action)
            .postJson<Record<string, number>>({ outboxIds: [item.id] })
            .pipe(
                timeout(30000),
                finalize(() => {
                    this.outboxBusy.delete(item.id);
                    this.cd.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    if (action === 'retry') {
                        this.outboxFilter = 'PENDING';
                        this.snackBar.open(
                            'Evento na fila Pendentes — acompanhe o status (enviado ou erro)',
                            'OK',
                            { duration: 6000 }
                        );
                        this.loadOutbox();
                        // Atualiza de novo após o drain processar
                        setTimeout(() => this.loadOutbox(), 2500);
                    } else {
                        this.outboxFilter = 'DISCARDED';
                        this.snackBar.open('Evento descartado — não será reenviado automaticamente', 'OK', {
                            duration: 5000
                        });
                        this.loadOutbox();
                    }
                },
                error: () =>
                    this.snackBar.open('Falha na operação (API indisponível ou tempo esgotado)', 'OK', {
                        duration: 5000
                    })
            });
    }

    trackOutbox(_: number, i: OutboxItem): string {
        return i.id;
    }

    get hasNext(): boolean {
        return this.data.items.length === this.pageSize;
    }

    get hasFailed(): boolean {
        return (this.data.counts['FAILED'] || 0) > 0;
    }

    setFilter(f: Filter): void {
        this.viewMode = 'animals';
        this.filter = f;
        this.page = 0;
        this.load();
        this.cd.markForCheck();
    }

    goto(delta: number): void {
        this.page = Math.max(0, this.page + delta);
        this.load();
    }

    load(): void {
        this.state = 'loading';
        this.cd.markForCheck();
        let params = new HttpParams().set('page', this.page).set('size', this.pageSize);
        const unsent = this.filter === 'UNSENT';
        if (unsent && this.search.trim()) params = params.set('q', this.search.trim());
        if (this.filter && !unsent) params = params.set('status', this.filter);
        this.api.root
            .path('integration-sync', unsent ? 'unsent' : 'items')
            .getJson<SyncResponse>({ params })
            .pipe(timeout(15000))
            .subscribe({
                next: (r) => {
                    this.data = unsent ? { counts: this.data.counts, items: r.items } : r;
                    this.state = 'ready';
                    this.cd.markForCheck();
                },
                error: (e) => {
                    this.state = e?.status === 404 ? 'unavailable' : 'error';
                    this.cd.markForCheck();
                }
            });
    }

    get integrationUnavailable(): boolean {
        return this.state === 'unavailable' || this.outboxState === 'unavailable';
    }

    retry(item: SyncItem): void {
        if (!this.confirmEnv()) return;
        this.retrying.add(item.animalId);
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'retry')
            .postJson<RetryResult[]>({ animalId: item.animalId })
            .pipe(
                timeout(60000),
                finalize(() => {
                    this.retrying.delete(item.animalId);
                    this.cd.markForCheck();
                })
            )
            .subscribe({ next: (r) => this.afterRetry(r), error: () => this.failed() });
    }

    retryAll(): void {
        if (!this.confirmEnv('Reenviar todos os itens com erro (até 50)?')) return;
        this.retryingAll = true;
        this.cd.markForCheck();
        this.api.root
            .path('integration-sync', 'retry-failed')
            .postJson<RetryResult[]>({})
            .pipe(
                timeout(300000),
                finalize(() => {
                    this.retryingAll = false;
                    this.cd.markForCheck();
                })
            )
            .subscribe({ next: (r) => this.afterRetry(r), error: () => this.failed() });
    }

    trackById(_: number, i: SyncItem): string {
        return i.animalId;
    }

    private confirmEnv(message = 'Reenviar este item ao SRG-SCL?'): boolean {
        return currentApiEnvKey() !== 'production' || confirm(`${message}\n\nAmbiente: ${currentApiEnvironment().label}`);
    }

    private failed(): void {
        this.snackBar.open('Falha ao reenviar (API indisponível ou tempo esgotado)', 'OK', { duration: 5000 });
    }

    private afterRetry(results: RetryResult[]): void {
        const ok = results.filter((r) => r.ok).length;
        this.snackBar.open(`${ok} enviado(s), ${results.length - ok} com erro`, 'OK', { duration: 5000 });
        this.load();
    }
}
